// ---------------------------------------------------------------------------
// Oração de hoje: fala com o banco (oracao_de_hoje, minha_oracao_situacao,
// marcar_rezei e a prévia do admin) usando o MESMO aparelho que o app já
// guarda em localStorage (seliga_aparelho). Nada é criado no banco daqui.
// Tudo é silencioso: quando algo falha, devolvemos null/[] para a tela nunca
// quebrar.
// ---------------------------------------------------------------------------

import { supabase } from './supabase';

const CHAVE_APARELHO = 'seliga_aparelho';

// Endereço público da oração (usado no "Enviar para alguém").
export const LINK_ORACAO = 'https://www.seligacatolico.com.br/oracao';

// Interruptor da linha "N pessoa(s) rezou/rezaram hoje pelo Se Liga".
// Desligado a pedido: a contagem deixa de aparecer em qualquer tela.
// Para voltar a mostrar, basta trocar para true. A contagem chega junto com
// a situação da oração (mesma chamada), então nada mais precisa ser ligado.
export const MOSTRAR_QUANTOS_REZARAM = false;

// Mesmo id de aparelho do app; cria sob demanda se ainda não existir.
export function idDoAparelho(): string | null {
  try {
    let id = localStorage.getItem(CHAVE_APARELHO);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(CHAVE_APARELHO, id);
    }
    return id;
  } catch {
    return null;
  }
}

export interface OracaoHoje {
  tem: boolean;
  dia?: string;
  evangelho_ref?: string | null;
  tema?: string | null;
  tempo_liturgico?: string | null;
  cor_liturgica?: string | null;
  santo?: string | null;
  santo_titulo?: string | null;
  santo_texto?: string | null;
  santo_pedido?: string | null;
  leitura_guia?: string | null;
  resumo?: string | null;
  reflexao?: string | null;
  perguntas?: string | null;
  texto?: string | null;
  silencio?: string | null;
  gesto?: string | null;
  catecismo?: string | null;
  aviso?: string | null;
  imagem_url?: string | null;
  imagem_credito?: string | null;
  video_url?: string | null;
  video_autor?: string | null;
  /** Só na prévia do admin. */
  aprovada?: boolean;
}

export interface SituacaoOracao {
  rezei_hoje: boolean;
  dias_na_semana: number;
  pessoas_hoje: number;
  aceitei_desafio: boolean;
  desafios_hoje: number;
  /** 7 dias (6 dias atrás → hoje); true = rezou naquele dia. */
  semana: boolean[];
}

export interface ResultadoRezei extends SituacaoOracao {
  ok: boolean;
}

// ---- Normalizadores (o banco devolve jsonb) ----

function objeto(data: unknown): Record<string, unknown> | null {
  if (!data) return null;
  if (Array.isArray(data)) return (data[0] as Record<string, unknown>) ?? null;
  if (typeof data === 'object') return data as Record<string, unknown>;
  return null;
}

function texto(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}

function numero(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// ---- Leituras ----

export async function oracaoDeHoje(): Promise<OracaoHoje | null> {
  try {
    const { data, error } = await supabase.rpc('oracao_de_hoje');
    if (error) return null;
    const d = objeto(data);
    if (!d) return null;
    if (d.tem !== true) return { tem: false };
    return {
      tem: true,
      dia: texto(d.dia) ?? undefined,
      evangelho_ref: texto(d.evangelho_ref),
      tema: texto(d.tema),
      tempo_liturgico: texto(d.tempo_liturgico),
      cor_liturgica: texto(d.cor_liturgica),
      santo: texto(d.santo),
      santo_titulo: texto(d.santo_titulo),
      santo_texto: texto(d.santo_texto),
      santo_pedido: texto(d.santo_pedido),
      leitura_guia: texto(d.leitura_guia),
      resumo: texto(d.resumo),
      reflexao: texto(d.reflexao),
      perguntas: texto(d.perguntas),
      texto: texto(d.texto),
      silencio: texto(d.silencio),
      gesto: texto(d.gesto),
      catecismo: texto(d.catecismo),
      aviso: texto(d.aviso),
      imagem_url: texto(d.imagem_url),
      imagem_credito: texto(d.imagem_credito),
      video_url: texto(d.video_url),
      video_autor: texto(d.video_autor),
    };
  } catch {
    return null;
  }
}

export async function minhaOracaoSituacao(): Promise<SituacaoOracao | null> {
  const aparelho = idDoAparelho();
  if (!aparelho) return null;
  try {
    const { data, error } = await supabase.rpc('minha_oracao_situacao', {
      p_aparelho: aparelho,
    });
    if (error) return null;
    const d = objeto(data);
    if (!d) return null;
    return situacaoDe(d);
  } catch {
    return null;
  }
}

function situacaoDe(d: Record<string, unknown>): SituacaoOracao {
  return {
    rezei_hoje: d.rezei_hoje === true,
    dias_na_semana: numero(d.dias_na_semana),
    pessoas_hoje: numero(d.pessoas_hoje),
    aceitei_desafio: d.aceitei_desafio === true,
    desafios_hoje: numero(d.desafios_hoje),
    semana: semanaDe(d.semana),
  };
}

// 7 dias, sempre com 7 posições; qualquer coisa estranha vira "não rezou".
function semanaDe(v: unknown): boolean[] {
  const out = [false, false, false, false, false, false, false];
  if (Array.isArray(v)) {
    for (let i = 0; i < 7 && i < v.length; i += 1) out[i] = v[i] === true;
  }
  return out;
}

export async function marcarRezei(): Promise<ResultadoRezei | null> {
  const aparelho = idDoAparelho();
  if (!aparelho) return null;
  try {
    const { data, error } = await supabase.rpc('marcar_rezei', { p_aparelho: aparelho });
    if (error) return null;
    const d = objeto(data);
    if (!d) return null;
    return { ok: d.ok === true, ...situacaoDe(d) };
  } catch {
    return null;
  }
}

// Aceita o desafio do dia (gesto). Mesmo retorno do marcar_rezei.
export async function aceitarDesafio(): Promise<ResultadoRezei | null> {
  const aparelho = idDoAparelho();
  if (!aparelho) return null;
  try {
    const { data, error } = await supabase.rpc('aceitar_desafio', { p_aparelho: aparelho });
    if (error) return null;
    const d = objeto(data);
    if (!d) return null;
    return { ok: d.ok === true, ...situacaoDe(d) };
  } catch {
    return null;
  }
}

// Prévia do admin: carrega um dia específico. Só responde para admin.
export async function oracoesAdminListar(dia: string, dias = 1): Promise<OracaoHoje[]> {
  try {
    const { data, error } = await supabase.rpc('oracoes_admin_listar', {
      p_de: dia,
      p_dias: dias,
    });
    if (error) return [];
    const lista = Array.isArray(data) ? data : [];
    return lista.map((item) => {
      const d = (item ?? {}) as Record<string, unknown>;
      return {
        tem: true,
        dia: texto(d.dia) ?? undefined,
        evangelho_ref: texto(d.evangelho_ref),
        tema: texto(d.tema),
        tempo_liturgico: texto(d.tempo_liturgico),
        cor_liturgica: texto(d.cor_liturgica),
        santo: texto(d.santo),
        santo_titulo: texto(d.santo_titulo),
        santo_texto: texto(d.santo_texto),
        santo_pedido: texto(d.santo_pedido),
        leitura_guia: texto(d.leitura_guia),
        resumo: texto(d.resumo),
        reflexao: texto(d.reflexao),
        perguntas: texto(d.perguntas),
        texto: texto(d.texto),
        silencio: texto(d.silencio),
        gesto: texto(d.gesto),
        catecismo: texto(d.catecismo),
        aviso: texto(d.aviso),
        imagem_url: texto(d.imagem_url),
        imagem_credito: texto(d.imagem_credito),
        video_url: texto(d.video_url),
        video_autor: texto(d.video_autor),
        aprovada: d.aprovada === true,
      };
    });
  } catch {
    return [];
  }
}

export async function souAdminPlataforma(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('sou_admin_plataforma');
    if (error) return false;
    return data === true;
  } catch {
    return false;
  }
}

// ---- Datas e textos ----

// 'YYYY-MM-DD' é lida como data local (nunca UTC), para não voltar um dia.
export function dataLocalDe(dia?: string | null): Date | null {
  if (!dia) return null;
  const m = dia.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

// Ex.: "Quinta-feira, 15 de outubro".
export function dataLongaPt(d: Date): string {
  const s = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(d);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function textoDias(n: number): string {
  return n === 1 ? '1 dia nesta semana' : `${n} dias nesta semana`;
}

export function textoPessoas(n: number): string {
  return n === 1 ? '1 pessoa rezou hoje pelo Se Liga' : `${n} pessoas rezaram hoje pelo Se Liga`;
}

export function textoDesafios(n: number): string {
  return n === 1 ? '1 pessoa aceitou hoje' : `${n} pessoas aceitaram hoje`;
}

// Cor litúrgica do dia → tema escuro da "capela". Sem cor (ou desconhecida):
// a paleta de "branco" (dourado sobre preto quente).
export interface CorLiturgica {
  nome: string;
  /** Fundo principal (o "corpo" da capela). */
  nave: string;
  /** Fundo secundário (um tom acima da nave). */
  nave2: string;
  /** Cor da fita de missal (marcador). */
  fita: string;
  /** Brilho radial suave no alto da página. */
  brilho: string;
  /** Compatibilidade: fundo/acento antigos. */
  fundo: string;
  acento: string;
}

const OURO_FORTE = '#E8C877';

const CORES_LITURGICAS: Record<string, CorLiturgica> = {
  verde: {
    nome: 'verde',
    nave: '#0E1C15',
    nave2: '#17291F',
    fita: '#2E8B57',
    brilho: 'rgba(46,139,87,.35)',
    fundo: '#0E1C15',
    acento: OURO_FORTE,
  },
  branco: {
    nome: 'branco',
    nave: '#19150F',
    nave2: '#262017',
    fita: '#EFE3BF',
    brilho: 'rgba(217,178,95,.30)',
    fundo: '#19150F',
    acento: OURO_FORTE,
  },
  vermelho: {
    nome: 'vermelho',
    nave: '#1C0D0E',
    nave2: '#2A1416',
    fita: '#B3261E',
    brilho: 'rgba(179,38,30,.35)',
    fundo: '#1C0D0E',
    acento: OURO_FORTE,
  },
  roxo: {
    nome: 'roxo',
    nave: '#150F1F',
    nave2: '#221833',
    fita: '#6E3FA3',
    brilho: 'rgba(110,63,163,.38)',
    fundo: '#150F1F',
    acento: OURO_FORTE,
  },
  rosa: {
    nome: 'rosa',
    nave: '#1E1117',
    nave2: '#2C1A23',
    fita: '#D46C95',
    brilho: 'rgba(212,108,149,.32)',
    fundo: '#1E1117',
    acento: OURO_FORTE,
  },
  preto: {
    nome: 'preto',
    nave: '#121212',
    nave2: '#1D1D1D',
    fita: '#2A2A2A',
    brilho: 'rgba(217,178,95,.18)',
    fundo: '#121212',
    acento: OURO_FORTE,
  },
};

export function corLiturgica(cor?: string | null): CorLiturgica {
  const chave = (cor ?? '').trim().toLowerCase();
  return CORES_LITURGICAS[chave] ?? CORES_LITURGICAS.branco;
}